import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner
} from "reactstrap";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import validateUrl from "campaign/landing-page/url-validator";
import mixpanelService from "common/mixpanel-service";
import pageData from "common/page-data";
import { selectAccount } from "../../account/account-selectors";
import { useUpdateDestinationPageMutation } from "../campaigns-api-endpoints";

const propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentUrl: PropTypes.string,
  saveUrl: PropTypes.string.isRequired,
  campaignType: PropTypes.string
};

const EditDestinationPageModal = ({
  isOpen,
  onClose,
  currentUrl = null,
  saveUrl,
  campaignType = null
}) => {
  const { campaignId } = useParams();
  const account = useSelector(selectAccount);
  const [url, setUrl] = useState(currentUrl || "");
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const [updateDestinationPage, { isLoading }] = useUpdateDestinationPageMutation();

  useEffect(() => {
    if (isOpen) {
      setUrl(currentUrl || "");
      setError(null);
      setWarning(null);
    }
  }, [isOpen, currentUrl]);

  const handleUrlChange = e => {
    const newUrl = e.target.value;
    if (/http:\/\//i.test(newUrl)) {
      setWarning(
        "For the best ad delivery and widest reach, we recommend using a secure (https://) URL."
      );
    } else {
      setWarning(null);
    }
    setUrl(newUrl);
    setError(null);
  };

  const handleSave = async e => {
    if (e) e.preventDefault();

    const { isValid, error: validationError } = validateUrl(url);
    if (!isValid) {
      setError(validationError);
      return;
    }

    try {
      await updateDestinationPage({
        saveUrl,
        url,
        accountId: account.id,
        campaignId
      }).unwrap();
      mixpanelService.track("Campaign.UpdateLandingPage", {
        ...pageData.get("mixpanelData"),
        campaign_id: campaignId,
        campaign_type: campaignType
      });
      onClose();
    } catch (err) {
      setError(typeof err.data === "string" ? err.data : "An error occurred. Please try again.");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      fade={false}
      className="account-app edit-destination-page-modal"
    >
      <ModalHeader toggle={onClose} className="p-2" />
      <Form onSubmit={handleSave}>
        <ModalBody className="px-4 py-0">
          <h5 className="font-weight-bold text-center">Edit Your Campaign Destination Page</h5>
          <hr />
          {error && (
            <div className="notifications danger">
              <div className="notifications__error-message">{error}</div>
            </div>
          )}
          {warning && (
            <div className="notifications warning">
              <div className="notifications__error-message">{warning}</div>
            </div>
          )}
          <ul className="mb-3 list-bulleted">
            <li>
              When setting the website, it is important that the page meet brand identity standards,
              contain proper agent information including the license number and property
              disclaimers, when required.
            </li>
            <li>Changes will take up to 1 business day to take effect.</li>
            <li>The campaign will not run while the ad is being updated.</li>
          </ul>
          <FormGroup>
            <Label for="destinationUrl">Enter Website</Label>
            <Input
              type="text"
              id="destinationUrl"
              value={url}
              onChange={handleUrlChange}
              placeholder="https://example.com"
              data-testid="destination-url-input"
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter className="pt-2 px-4">
          <Button color="secondary" onClick={onClose} disabled={isLoading} className="btn-sharp">
            Cancel
          </Button>
          <Button color="primary" type="submit" className="btn-sharp" disabled={isLoading}>
            {isLoading ? (
              <>
                <Spinner color="light" size="sm" /> Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

EditDestinationPageModal.propTypes = propTypes;

export default EditDestinationPageModal;
